import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./Footer";
import { blogPosts } from "./blogPosts";
import { ArrowLeft, Clock3 } from "lucide-react";

export default function BlogArticlePage() {
  const { id } = useParams();

  const post = blogPosts.find(
    (item) => String(item.id) === String(id)
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F8F8FB]">
        <Navbar variant="light" />

        <main className="flex min-h-[70vh] items-center justify-center px-6 pt-24">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-[#111827]">
              Article not found
            </h1>

            <p className="mt-4 text-gray-600">
              The post you are looking for does not exist.
            </p>

            <Link
              to="/blog"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#355dff] px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8FB]">
      <Navbar variant="light" />

      <main className="mx-auto max-w-5xl px-6 pb-20 pt-32">
        {/* Back Button */}
        <Link
          to="/blog"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-[#355dff] transition hover:text-blue-700"
        >
          <ArrowLeft size={16} />
          Back to blog
        </Link>

        {/* Article */}
        <article className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm">
          {/* Hero Image */}
          <div className="overflow-hidden">
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 md:p-10 lg:p-12">
            {/* Category */}
            <span className="inline-flex rounded-full bg-[#EBF2FF] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#355dff]">
              {post.category}
            </span>

            {/* Title */}
            <h1 className="mt-6 text-3xl font-extrabold leading-tight text-[#111827] md:text-5xl">
              {post.title}
            </h1>

            {/* Read Time */}
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2">
                <Clock3 size={16} className="text-[#355dff]" />
                {post.readTime}
              </span>
            </div>

            {/* Divider */}
            <div className="mt-8 h-1 w-24 rounded-full bg-blue-600" />

            {/* Dynamic Article Content */}
            <div className="mt-10">
              {post.content.map((block, index) => {
                /* --------------------------------
                   HEADING
                -------------------------------- */
                if (block.type === "heading") {
                  return (
                    <h2
                      key={index}
                      className="mt-12 mb-5 text-2xl font-bold leading-tight text-[#111827] md:text-3xl"
                    >
                      {block.text}
                    </h2>
                  );
                }

                /* --------------------------------
                   SUBHEADING
                -------------------------------- */
                if (block.type === "subheading") {
                  return (
                    <h3
                      key={index}
                      className="mt-8 mb-3 text-xl font-semibold leading-tight text-[#111827] md:text-2xl"
                    >
                      {block.text}
                    </h3>
                  );
                }

                /* --------------------------------
                   PARAGRAPH
                -------------------------------- */
                if (block.type === "paragraph") {
                  return (
                    <p
                      key={index}
                      className="mb-6 text-[17px] leading-8 text-gray-700"
                    >
                      {block.text}
                    </p>
                  );
                }

                /* --------------------------------
                   BULLET LIST
                -------------------------------- */
                if (block.type === "list") {
                  return (
                    <ul
                      key={index}
                      className="mb-8 ml-6 list-disc space-y-3 text-[17px] leading-8 text-gray-700"
                    >
                      {block.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="pl-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }

                /* --------------------------------
                   QUOTE
                -------------------------------- */
                if (block.type === "quote") {
                  return (
                    <blockquote
                      key={index}
                      className="my-10 rounded-r-2xl border-l-4 border-[#355dff] bg-[#F5F7FF] px-6 py-5 text-lg italic leading-8 text-gray-700 md:px-8 md:text-xl"
                    >
                      "{block.text}"
                    </blockquote>
                  );
                }

                return null;
              })}
            </div>

            {/* Bottom CTA */}
            <div className="mt-14 border-t border-gray-200 pt-8">
              <p className="text-sm text-gray-500">
                Interested in building your next digital product?
              </p>

              <Link
                to="/contact"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#355dff] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#355dff]/20 transition hover:bg-blue-700"
              >
                Talk to Orryworx
                <ArrowLeft
                  size={16}
                  className="rotate-180"
                />
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}